/* eslint-disable no-shadow */
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ReactPaginate from 'react-paginate';

import {
  Badge,
  Button,
  BUTTON_CONTEXT,
  getTranslation,
  Icon,
  Link,
  Loader,
  MapIcon,
  ResyCalendar,
  SearchCollection,
  SearchResult,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
  setMultiStepModalMode,
  toggleFavoriteVenue,
  TranslationProvider,
  utils,
} from '@resy/ui-kitchen';

import { MODAL_MODES } from '../../../../../ui-kitchen/src/reducers/favoriteSlice';
import { searchPropsType } from '../../types/searchProps';
import MultiStepListModal from '../../../../../ui-kitchen/src/components/MultiStepListModal';
import ListHeart from '../../../../../ui-kitchen/src/svg/icons/heart_lists.svg';

import './SearchResultsContainer.scss';

import FooterCondensed from '../../components/FooterCondensed';
import NoResults from '../../components/NoResults';
import ScrollTo from '../../components/ScrollTo';
import { Selector } from '../../components/Nav';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { selectIsGlobalDiningAccess } from '../../../../../ui-kitchen/src/reducers/userSlice';
import { getCollectionSuggestions } from '../../../../../ui-kitchen/src/api/api';
import { suggestionsTransform } from '../../../../../ui-kitchen/src/api/resyApiTransformer';
import SearchFilterContainer from '../../../../../ui-kitchen/src/components/SearchFilterContainer/SearchFilterContainer';
import {
  LIST_ID, RESY_CREDIT_ELIGIBLE_ONLY, TABLE_AREA, VENUE_TYPE,
} from '../../../../../ui-kitchen/src/components/SearchFilterContainer/searchFilterConstants';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { sanitizeString } from '../../../../../ui-kitchen/src/utils/sanitizeString';
import { selectSearchSessionId } from '../../../../../ui-kitchen/src/reducers/venueSearchSlice';
import useExperiment from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { JCE_INTEGRATION_WINERY } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';

// necessary to prevent erroneous snapshot changes
ReactPaginate.displayName = 'ReactPaginate';

const RGA_ID = 10;

function setTabIndex(page, nbPages) {
  // hack to prevent the previous/next buttons from being tabbable when disabled
  // react-paginate does not provide a mechanism for doing this
  const prevButton = document.querySelector('.Pagination__previous');

  const nextButton = document.querySelector('.Pagination__next');

  if (prevButton) {
    if (page === 1) {
      prevButton.children[0]?.setAttribute('tabIndex', '-1');
    } else {
      prevButton.children[0]?.setAttribute('tabIndex', '0');
    }
  }

  if (nextButton) {
    if (page === nbPages) {
      nextButton.children[0]?.setAttribute('tabIndex', '-1');
    } else {
      nextButton.children[0]?.setAttribute('tabIndex', '0');
    }
  }
}

const SearchResultsContainer = ({
  $rootScope,
  Analytics,
  isAuthenticated,
  isMobile,
  isSearching,
  isWineRegion,
  toggleActiveView,
  cuisines,
  event,
  lists,
  hits,
  page,
  hitsPerPage,
  nbPages,
  query,
  reservationLimit,
  seating,
  selectedVenueId,
  selectedDate,
  selectedPartySize,
  selectedTime,
  userIsGda,
  collectionData,
  searchSessionId,
  selectedLocation,
  selectedCollection,
  showAuthModal,
  getQueuedData,
  setQueuedData,
  onClear,
  onVenueCardHover,
  onSearchResultSelect,
  onPageChange,
  toggleFavoriteVenue,
  setMultiStepModalVenueContext,
  setMultiStepModalVenueId,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
}) => {
  const [isVenueTypePillsEnabled] = useExperiment(JCE_INTEGRATION_WINERY);
  const [suggestions, setSuggestions] = useState([]);
  const [apiError, setApiError] = useState(null);
  const [displayMoreCollections, setDisplayMoreCollections] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isCalendarSticky, setIsCalendarSticky] = useState(false);

  const toggleFavorite = useCallback((response) => {
    const { favorite, venue_id: venueId } = response;
    if (isAuthenticated) {
      try {
        toggleFavoriteVenue({ venueId, favorite });
      } catch (err) {
        setQueuedData(response);
      }
    } else {
      setQueuedData(response);
      showAuthModal();
    }
  }, [isAuthenticated, setQueuedData, showAuthModal, toggleFavoriteVenue]);

  useEffect(() => {
    const fetchCollectionSuggestions = async () => {
      const partySize = selectedPartySize;
      const locationId = selectedLocation.code;

      try {
        const data = await getCollectionSuggestions({ locationId, selectedDate, partySize });
        const result = suggestionsTransform(data);
        setSuggestions(result);
      } catch (error) {
        setApiError(error);
      }
    };
    fetchCollectionSuggestions();
  }, [selectedDate, selectedLocation.code, selectedPartySize]);

  useEffect(() => {
    try {
      if (isAuthenticated) {
        const queuedData = getQueuedData(['favorite', 'venue_id']);
        if (queuedData) {
          toggleFavorite(queuedData);
        }
      }
    } catch (err) {
      Logger.error('[SearchResultsContainer] Error getting queued data:', err);
    }
  }, [getQueuedData, isAuthenticated, toggleFavorite]);

  useEffect(() => {
    setTabIndex(page, nbPages);
  }, [page, nbPages]);

  const handleLearnMoreClicked = () => {
    utils.openExternalUrl(selectedCollection.learnMoreLink);
  };

  const handleHeartIconClick = (response) => {
    const { venue_id: venueId, venue } = response;

    const showMultiListModal = () => {
      setMultiStepModalVenueContext({ name: venue?.name, city: venue?.location?.name });
      setMultiStepModalVenueId(venueId);
      setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST);
      setMultiStepModalOpenState(true);
    };

    if (isAuthenticated) {
      showMultiListModal();
    } else {
      showAuthModal(showMultiListModal);
    }
  };

  const closeCalendar = () => {
    setIsCalendarOpen(false);
  };

  const setCalendarSticky = () => {
    setIsCalendarSticky((prevIsCalendarStickyState) => !prevIsCalendarStickyState);
  };

  const toggleCalendar = () => {
    setIsCalendarOpen((prevIsCalendarOpenState) => !prevIsCalendarOpenState);
  };

  const toggleDisplayMoreCollections = () => {
    setDisplayMoreCollections((prevDisplayMoreCollectionState) => !prevDisplayMoreCollectionState);
  };

  const isVenueSelected = (id) => selectedVenueId === id;

  const isHitList = () => selectedCollection && selectedCollection.type_id === 8;

  const onVenueClick = (venue, index) => {
    Analytics.event('click search result', {
      searchSessionId,
      'venue name': venue.name,
      'rank on page': index + 1,
      'page number': page,
      'overall rank': (hitsPerPage * (page - 1)) + index + 1,
    });
  };

  const renderClearButton = () => {
    if (isHitList() && !hits.length) {
      const city = selectedLocation.name;

      return (
        <div className="emptyCity">
          <TranslationProvider translationKey="your_city_is_empty" translationValues={{ city }}>
            <div className="emptyCity__title">
              Your
              {city}
              Hit List is empty.
            </div>
          </TranslationProvider>
          <TranslationProvider translationKey="keep_track">
            <div>Keep track of the restaurants you love with your personal Hit List.</div>
          </TranslationProvider>
        </div>
      );
    }

    if (!hits.length && selectedCollection) {
      return <NoResults onClear={onClear} query={query} />;
    }

    return null;
  };

  const renderResults = () => {
    const { searchProps } = $rootScope;
    const seatsSelected = (selectedPartySize || searchProps?.selectedSeats);
    const timeSelected = (selectedTime || searchProps?.selectedTime);
    const titleText = (
      <TranslationProvider translationKey="reservation_date">
        <h6 className="ResyCalendar__title">Reservation Date</h6>
      </TranslationProvider>
    );

    return (
      <div className="SearchResultsContainer__results">
        <div className={cx('SearchResultsContainer__selectors-filters', { 'SearchResultsContainer__selectors-filters--not-mobile': !isMobile })}>
          <div className={cx('SearchResultsContainer__selectors', { 'SearchResultsContainer__selectors--mobile': isMobile })}>
            <Selector
              date={searchProps?.searchFields?.datepicker?.config?.label}
              isMobile={isMobile}
              onPartySizeChange={searchProps?.onPartySizeChange}
              onPartySizeClick={closeCalendar}
              onTimeChange={searchProps?.onTimeChange}
              onTimeClick={closeCalendar}
              showTimeFilter
              time={timeSelected}
              toggleCalendar={toggleCalendar}
              quantitySelected={seatsSelected}
              setCalendarSticky={setCalendarSticky}
              calendarExpanded={searchProps?.dropdowns?.date}
            />
          </div>
          <div className="SearchResultsContainer__filters">
            <SearchFilterContainer
              $rootScope={$rootScope}
              Analytics={Analytics}
              cuisines={cuisines}
              isAuthenticated={isAuthenticated}
              isMobile={isMobile}
              lists={lists}
              seating={seating}
              urlSlug={selectedLocation.url_slug}
            />
          </div>
        </div>
        {query?.type === 'facet' && query?.value?.split(':')[0] === RESY_CREDIT_ELIGIBLE_ONLY && (
          <div className="SearchResultsContainer__resy-credit-description">
            <TranslationProvider translationKey="search_resy_credit_description">
              <span>
                These venues currently qualify for the Resy Credit, a benefit for select American Express® Card Members.
              </span>
            </TranslationProvider>
            {' '}
            <TranslationProvider translationKey="search_resy_credit_terms">
              <a
                className="SearchResultsContainer__resy-credit-description__terms"
                href="https://resy.com/resycredit"
                target="_blank"
                rel="noreferrer"
              >
                Terms apply.
              </a>
            </TranslationProvider>
          </div>
        )}
        {isCalendarOpen && (
          <div
            id="ResyNav__calendar-dialog"
            className={cx('ResyNav__calendar-container', { 'ResyNav__calendar-container--sticky': isCalendarSticky })}
            role="dialog"
          >
            <ResyCalendar
              Analytics={Analytics}
              currentMonth={moment(selectedDate)}
              calendarMap={utils.venueInventoryUtils.getDateRange(moment())}
              focusOnClose="#DateSelector__button"
              clickableElementIdsToIgnore={['DateSelector__button']}
              closeCalendar={toggleCalendar}
              getAriaLabel={utils.venueInventoryUtils.getAriaLabel}
              numberOfMonths={1}
              onClick={(dateEvent) => { searchProps?.onDateSelect(dateEvent, { usesBackdrop: false }); }}
              selectedDate={moment(selectedDate).format('YYYY-MM-DD')}
              titleText={titleText}
              venueCity="Search"
            />
          </div>
        )}

        <div className="SearchResultsContainer__result-list">
          {hits.map((venue, index) => (
            <ScrollTo
              key={venue.id}
              behavior="smooth"
              offset={-125}
              when={isVenueSelected(venue.id)}
            >
              <SearchResult
                $rootScope={$rootScope}
                activeNotifications={venue.activeNotifications}
                canFavorite
                context={BUTTON_CONTEXT.SEARCH_RESULT}
                event={event}
                isMobile={isMobile}
                layout="row"
                onClick={() => onSearchResultSelect(venue)}
                onKeyPress={() => onSearchResultSelect(venue)}
                onVenueCardHover={onVenueCardHover}
                onFavoriteClick={handleHeartIconClick}
                onReservationButtonClick={() => onVenueClick(venue, index)}
                onVenueClick={() => onVenueClick(venue, index)}
                reservationLimit={reservationLimit}
                selectedDate={selectedDate}
                selectedSeats={selectedPartySize}
                urlSlug={selectedLocation.url_slug}
                userIsGda={userIsGda}
                venue={venue}
                venueEvents={venue.events}
                isAuthenticated={isAuthenticated}
                showAuthModal={showAuthModal}
              />
            </ScrollTo>
          ))}
        </div>
        {renderClearButton()}
      </div>
    );
  };

  const renderSearchBackBtn = () => (
    <TranslationProvider translationKey="search_all_results">
      <Button
        className="SearchResultsContainer__back-btn"
        data-test-id="search_results_container-button-all_results"
        onClick={onClear}
      >
        &lsaquo; All Results
      </Button>
    </TranslationProvider>
  );

  const renderNumberOfVenues = () => {
    if (nbPages === 1) {
      const numOfResults = hits?.length;
      return (
        (numOfResults === 1)
          ? (
            <TranslationProvider
              translationKey="search_one_result"
            >
              <p>1 venue</p>
            </TranslationProvider>
          )
          : (
            <TranslationProvider
              translationKey="search_num_results"
              translationValues={{ hits: numOfResults.toString() }}
            >
              <p>
                {`${numOfResults} venues`}
              </p>
            </TranslationProvider>
          )
      );
    }

    const estNumOfResults = Math.round(hitsPerPage * (nbPages - 1));
    return (
      <TranslationProvider
        translationKey="search_est_num_results"
        translationValues={{ hits: estNumOfResults.toString() }}
      >
        <p>
          {`${estNumOfResults}+ venues`}
        </p>
      </TranslationProvider>
    );
  };

  const renderMetadata = () => {
    const { searchProps } = $rootScope;
    // Search for facet value
    const key = query.value.split(':')[0];
    if (query?.type === 'facet' && key === RESY_CREDIT_ELIGIBLE_ONLY) {
      return (
        <>
          {renderSearchBackBtn()}
          <div className="SearchResultsContainer__metadata SearchResultsContainer__metadata--facet-container">
            <div
              className="SearchResultsContainer__metadata-content"
              aria-live="assertive"
              role="alert"
              aria-atomic="true"
            >
              <TranslationProvider
                translationKey="search_headline_resy_credit"
                translationValues={{ city: selectedLocation.name }}
              >
                <h1 className="SearchResultsContainer__metadata--facet-container__value color--text-primary">
                  {`Resy Credit eligible restaurants in ${selectedLocation.name}`}
                </h1>
              </TranslationProvider>
              {renderNumberOfVenues()}
            </div>
          </div>
        </>
      );
    }

    if (query?.type === 'facet' && key.indexOf(LIST_ID) > -1) {
      const value = query.value.split(':')[1];
      const [list] = lists.filter((item) => (item.id === value));
      return (
        <>
          {renderSearchBackBtn()}
          <div className="SearchResultsContainer__metadata SearchResultsContainer__metadata--facet-container">
            <div className="SearchResultsContainer__metadata-row">
              <div className="SearchResultsContainer__icon-container">
                <ListHeart className="SearchResultsContainer__icon-list" />
              </div>
              <div
                className="SearchResultsContainer__metadata-content"
                aria-live="assertive"
                role="alert"
                aria-atomic="true"
              >
                <TranslationProvider
                  translationKey="search_headline_collection"
                  translationValues={{ collection: value, city: selectedLocation.name }}
                >
                  <h1 className="SearchResultsContainer__metadata--facet-container__value color--text-primary">
                    {list && list.name}
                  </h1>
                </TranslationProvider>
                {renderNumberOfVenues()}
              </div>
            </div>
          </div>
        </>
      );
    }

    if (query?.type === 'facet' && key === VENUE_TYPE) {
      const value = query.value.split(':')[1];
      const isWinery = value === 'wineries';
      return (
        <>
          {renderSearchBackBtn()}
          <div className="SearchResultsContainer__metadata SearchResultsContainer__metadata--facet-container">
            <div
              className="SearchResultsContainer__metadata-content"
              aria-live="assertive"
              role="alert"
              aria-atomic="true"
            >
              <TranslationProvider
                translationKey={isWinery ? 'search_headline_venue_type_wineries' : 'search_headline_venue_type_restaurants'}
                translationValues={{ city: selectedLocation.name }}
              >
                <h1 className="SearchResultsContainer__metadata--facet-container__value color--text-primary">
                  {isWinery ? `All Wineries near ${selectedLocation.name}` : `All Restaurants near ${selectedLocation.name}`}
                </h1>
              </TranslationProvider>
              {renderNumberOfVenues()}
            </div>
          </div>
        </>
      );
    }

    if (query?.type === 'facet' && key.indexOf(LIST_ID) === -1) {
      const value = query.value.split(':')[1];
      return (
        <>
          {renderSearchBackBtn()}
          <div className="SearchResultsContainer__metadata SearchResultsContainer__metadata--facet-container">
            <div className="SearchResultsContainer__metadata-content">
              <div
                aria-live="assertive"
                role="alert"
                aria-atomic="true"
              >
                {key.indexOf(TABLE_AREA) > -1
                  ? (
                    <TranslationProvider
                      translationKey="search_headline_collection_tables"
                      translationValues={{ seating: value, city: selectedLocation.name }}
                    >
                      <h1 className="SearchResultsContainer__metadata--facet-container__value color--text-primary">
                        {`${value} tables near ${selectedLocation.name}`}
                      </h1>
                    </TranslationProvider>
                  )
                  : (
                    <TranslationProvider
                      translationKey="search_headline_collection"
                      translationValues={{ collection: value, city: selectedLocation.name }}
                    >
                      <h1 className="SearchResultsContainer__metadata--facet-container__value color--text-primary">
                        {`${value} restaurants near ${selectedLocation.name}`}
                      </h1>
                    </TranslationProvider>
                  )}
                {renderNumberOfVenues()}
              </div>
            </div>
          </div>
        </>
      );
    }

    if (query && query.type === 'query' && query.value !== '') {
      return (
        <>
          {renderSearchBackBtn()}
          <div className="SearchResultsContainer__metadata SearchResultsContainer__metadata--facet-container">
            <div
              aria-live="assertive"
              role="alert"
              aria-atomic="true"
            >
              <TranslationProvider
                translationKey="search_headline_query"
                translationValues={{ query: query.value, city: selectedLocation.name }}
              >
                <h1 className="SearchResultsContainer__metadata--facet-container__value color--text-primary">
                  {`“${query.value}” restaurants near ${selectedLocation.name}`}
                </h1>
              </TranslationProvider>
              {renderNumberOfVenues()}
            </div>
          </div>
        </>
      );
    }

    if (selectedCollection) {
      const isGdaCollection = selectedCollection.type_id === RGA_ID;
      const sanitizedDescription = sanitizeString(selectedCollection.description);
      return (
        <>
          {renderSearchBackBtn()}
          <div className={
            cx(
              'SearchResultsContainer__metadata',
              'SearchResultsContainer__metadata--facet-container',
              {
                'SearchResultsContainer__metadata--isGda': isGdaCollection,
              }
            )
          }
          >
            <div className="SearchResultsContainer__metadata-row">
              {!isGdaCollection
                && (
                  <div className="SearchResultsContainer__icon-container">
                    <Badge
                      url={selectedCollection.icon.path}
                      label={null}
                      size={isMobile ? 27 : 34}
                      name={selectedCollection.icon.name}
                    />
                  </div>
                )}
              <div className="SearchResultsContainer__metadata-content">
                <div className="SearchResultsContainer__metadata-badge-text">
                  {isGdaCollection
                    && (
                      <Badge
                        url={selectedCollection.icon.path}
                        size={20}
                        name={selectedCollection.icon.name}
                      />
                    )}
                  {selectedCollection.eyebrow && (
                    <div className="SearchResultsContainer__metadata--eyebrow">
                      {selectedCollection.eyebrow}
                    </div>
                  )}
                </div>
                <h1 className="SearchResultsContainer__metadata--facet-container__value SearchResultsContainer__metadata--collection-name color--text-primary">
                  <span>{selectedCollection.name}</span>
                </h1>
                <div className="SearchResultsContainer__metadata--description body--sm">
                  {/* eslint-disable react/no-danger */}
                  <span dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
                  &nbsp;
                  {selectedCollection?.learnMore && (
                    <Link onClick={handleLearnMoreClicked} variant="inline">{selectedCollection?.learnMore}</Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      );
    }

    // Default search
    return (
      <>
        <div className="SearchResultsContainer__metadata SearchResultsContainer__metadata--facet-container">
          <div
            aria-live="assertive"
            role="alert"
            aria-atomic="true"
          >
            <TranslationProvider
              translationKey={isWineRegion && isVenueTypePillsEnabled ? 'search_headline_venue_type_all' : 'search_headline_default'}
              translationValues={{ city: selectedLocation.name }}
            >
              <h1 className="SearchResultsContainer__metadata--facet-container__value color--text-primary">
                {isWineRegion && isVenueTypePillsEnabled ? `All Venues near ${selectedLocation.name}` : `Restaurants near ${selectedLocation.name}`}
              </h1>
            </TranslationProvider>
            {renderNumberOfVenues()}
          </div>
          <SearchCollection
            collectionData={collectionData}
            isMobile={isMobile}
            setSelectedCollection={searchProps?.onCollectionSelect}
            displayMoreCollections={displayMoreCollections}
            toggleDisplay={toggleDisplayMoreCollections}
          />
        </div>
      </>
    );
  };

  const renderSuggestionCard = (venue) => (
    <SearchResult
      canFavorite
      event={event}
      isMobile={isMobile}
      isSuggestion
      key={venue.id}
      onFavoriteClick={handleHeartIconClick}
      showSlots={false}
      smallImage
      urlSlug={selectedLocation.url_slug}
      venue={venue}
    />
  );

  const renderSuggestions = () => {
    if (isHitList() && !apiError && suggestions.length > 0) {
      return (
        <div className="suggestions">
          <TranslationProvider translationKey="recommendations">
            <div className="suggestions__title">Recommended for You</div>
          </TranslationProvider>
          {suggestions.map(renderSuggestionCard)}
        </div>
      );
    }

    return null;
  };

  const renderLoadingOrResults = () => {
    if (isSearching) {
      return (
        <>
          <div
            aria-live="polite"
            role="status"
            aria-label={getTranslation({
              translationKey: 'communication_loading_message',
              fallback: 'Loading...',
            })}
          />
          <Loader className="PageLoader" />
        </>
      );
    }

    if (hits.length === 0 && !selectedCollection) {
      return <NoResults onClear={onClear} query={query} />;
    }

    return (
      <>
        {renderMetadata()}
        {renderResults()}
        {renderSuggestions()}
      </>
    );
  };

  const renderPagination = () => {
    if (nbPages > 1) {
      return (
        <ReactPaginate
          activeLinkClassName="selected"
          containerClassName="Pagination"
          disableInitialCallback
          forcePage={page - 1}
          marginPagesDisplayed={0}
          nextClassName="Pagination__next"
          nextLabel="&rsaquo;"
          onPageChange={onPageChange}
          pageClassName="Pagination__page"
          pageCount={nbPages}
          pageRangeDisplayed={isMobile ? 2 : 4}
          previousClassName="Pagination__previous"
          previousLabel="&lsaquo;"
        />
      );
    }
    return null;
  };

  return (
    <div className="SearchResultsContainer">
      <ScrollTo when={!isSearching} offset={isMobile ? -90 : -70}>
        {renderLoadingOrResults()}
        {renderPagination()}
        {isMobile && (
          <Button
            type="button"
            className="SearchResultsContainer__active-view-button"
            onClick={toggleActiveView}
          >
            <Icon
              component={MapIcon}
              name="map"
            />
            <TranslationProvider translationKey="map">
              <span>Map</span>
            </TranslationProvider>
          </Button>
        )}
        <FooterCondensed $rootScope={$rootScope} />
      </ScrollTo>
      <MultiStepListModal Analytics={Analytics} />
    </div>
  );
};

SearchResultsContainer.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
    hideSelectors: PropTypes.bool.isRequired,
    searchProps: searchPropsType,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  cuisines: PropTypes.arrayOf(PropTypes.object),
  collectionData: PropTypes.arrayOf(PropTypes.object),
  event: PropTypes.func.isRequired,
  getQueuedData: PropTypes.func.isRequired,
  hits: PropTypes.arrayOf(PropTypes.object).isRequired,
  hitsPerPage: PropTypes.number.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  isSearching: PropTypes.bool.isRequired,
  isWineRegion: PropTypes.bool,
  lists: PropTypes.arrayOf(PropTypes.object),
  nbPages: PropTypes.number.isRequired,
  onClear: PropTypes.func.isRequired,
  onVenueCardHover: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onSearchResultSelect: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  query: PropTypes.shape({
    type: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }),
  reservationLimit: PropTypes.number.isRequired,
  searchSessionId: PropTypes.string,
  seating: PropTypes.arrayOf(PropTypes.object),
  selectedCollection: PropTypes.shape({
    description: PropTypes.string.isRequired,
    eyebrow: PropTypes.string,
    name: PropTypes.string.isRequired,
    shortName: PropTypes.string.isRequired,
    type_id: PropTypes.number.isRequired,
    learnMore: PropTypes.string,
    learnMoreLink: PropTypes.string,
    icon: PropTypes.shape({
      path: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }),
  selectedDate: PropTypes.string.isRequired,
  selectedLocation: PropTypes.shape({
    code: PropTypes.string.isRequired,
    map_center: PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
    }),
    name: PropTypes.string.isRequired,
    radius: PropTypes.number.isRequired,
    url_slug: PropTypes.string.isRequired,
  }).isRequired,
  selectedPartySize: PropTypes.number,
  setQueuedData: PropTypes.func.isRequired,
  selectedTime: PropTypes.string.isRequired,
  selectedVenueId: PropTypes.number,
  showAuthModal: PropTypes.func.isRequired,
  toggleFavoriteVenue: PropTypes.func.isRequired,
  setMultiStepModalVenueContext: PropTypes.func.isRequired,
  setMultiStepModalVenueId: PropTypes.func.isRequired,
  setMultiStepModalMode: PropTypes.func.isRequired,
  setMultiStepModalOpenState: PropTypes.func.isRequired,
  toggleActiveView: PropTypes.func,
  userIsGda: PropTypes.bool.isRequired,
  userLocation: PropTypes.shape({
    lat: PropTypes.number,
    long: PropTypes.number,
  }).isRequired,
};

SearchResultsContainer.defaultProps = {
  collectionData: [],
  cuisines: undefined,
  isMobile: true,
  isWineRegion: false,
  lists: [],
  query: null,
  searchSessionId: '',
  seating: undefined,
  selectedCollection: null,
  selectedPartySize: null,
  selectedVenueId: null,
  toggleActiveView: null,
};

const mapStateToProps = (state) => ({
  isAuthenticated: selectIsAuthenticated(state),
  userIsGda: selectIsGlobalDiningAccess(state),
  searchSessionId: selectSearchSessionId(state),
  isWineRegion: state.resyLocations?.selected?.is_wine_region === true,
});

const mapDispatchToProps = {
  toggleFavoriteVenue,
  setMultiStepModalVenueContext,
  setMultiStepModalVenueId,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
};

SearchResultsContainer.displayName = 'SearchResultsContainer';

export { SearchResultsContainer as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(SearchResultsContainer);
